"use client";

import { FC, useContext, useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import classNames from "classnames";

import { Icon } from "components/atoms/Icon/Icon";
import { TranslatedLink } from "components/atoms/TranslatedLink/TranslatedLink";
import { DropdownMenu } from "components/molecules/DropdownMenu/DropdownMenu";
import { SLUGS } from "consts";
import { Breakpoints, IconType, IDatoGeneralMeta } from "@types";

import Link from "next/link";
import { HeaderContext, IHeaderContext } from "context/HeaderContext";
import { useBreakpoint } from "utils";
import { CBButton } from "components/InlineBlocks/CBButton";
import s from "./NavbarPrimary.module.scss";

interface Props {
  mainNavbarItems: IDatoGeneralMeta["mainNavigation"];
  navCta: IDatoGeneralMeta["navCta"];
}

export const NavbarPrimary: FC<Props> = ({ mainNavbarItems, navCta }) => {
  const t = useTranslations("common");
  const breakpoint = useBreakpoint();

  const { openFlyoutId, setOpenFlyoutId } = useContext(HeaderContext) as IHeaderContext;

  const [dropdownHover, setDropdownHover] = useState<string | null>();

  useEffect(() => {
    let timeOutId: NodeJS.Timeout | undefined;

    if (breakpoint !== Breakpoints.mobile) {
      if (dropdownHover) {
        clearTimeout(timeOutId);
        setOpenFlyoutId(dropdownHover);
      } else {
        timeOutId = setTimeout(() => {
          setOpenFlyoutId(null);
        }, 500);
      }
    }

    return () => clearTimeout(timeOutId);
  }, [dropdownHover, setOpenFlyoutId, breakpoint]);

  return (
    <nav className={s.Navbar} role="navigation" aria-label={t("primary-navigation")}>
      <ul className={s.List}>
        {mainNavbarItems?.map((item) => {
          if (item.__typename === "LinkRecord") {
            if (item.link?.__typename === "ExternalLinkRecord") {
              return (
                <li className={s.Item} key={item.id}>
                  <Link href={item.link.url}>
                    <a className={s.Link}>{item.linkText}</a>
                  </Link>
                </li>
              );
            }

            if (item.link?.__typename === "InternalLinkRecord") {
              return (
                <li className={s.Item} key={item.id}>
                  <TranslatedLink record={item.link.link} className={s.Link}>
                    {item.linkText}
                  </TranslatedLink>
                </li>
              );
            }
          }

          if (item?.__typename === "NavFlyoutGroupRecord") {
            return (
              <li className={`${s.Item} ${s.IsToggle}`} key={item.id}>
                <button
                  type="button"
                  className={classNames(s.ToggleButton, {
                    [s.IsOpen]: openFlyoutId === item.id,
                  })}
                  onClick={() =>
                    breakpoint === Breakpoints.mobile &&
                    setOpenFlyoutId(openFlyoutId === item.id ? null : item.id)
                  }
                  onMouseOver={() => setDropdownHover(item.id)}
                  onMouseLeave={() => setDropdownHover(null)}
                  onFocus={() => setDropdownHover(item.id)}
                  aria-expanded={openFlyoutId === item.id}
                  aria-controls={`${item.id}-menu`}
                >
                  <span className={s.ToggleButtonText}>{item.title}</span>
                  <span className={s.ToggleButtonIcon}>
                    <Icon name={IconType.CaretFilled} size="s" color="black" />
                  </span>
                </button>

                {openFlyoutId === item.id && (
                  <div
                    onMouseOver={() => setDropdownHover(item.id)}
                    onMouseLeave={() => setDropdownHover(null)}
                    onFocus={() => setDropdownHover(item.id)}
                  >
                    <DropdownMenu
                      id={`${item.title.toLowerCase().replace(/\s+/g, "-")}-menu`}
                      columns={item.columns}
                    />
                  </div>
                )}
              </li>
            );
          }

          return null;
        })}

        <li className={s.Search}>
          <TranslatedLink
            link={{ href: SLUGS.search }}
            className={s.SearchLink}
            aria-label={t("search")}
          >
            <Icon name={IconType.Search} size="l" />
          </TranslatedLink>
        </li>
      </ul>
      {navCta && (
        <div className={s.Cta}>
          <CBButton {...navCta} />
        </div>
      )}
    </nav>
  );
};
